import React, { useState, useEffect } from "react";
import { SERVER_BASE } from "../config/server";
import apiClient from "../utils/apiClient";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Select from 'react-select';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";

const IssueSlip = ({ accessOfDepartments }) => {
  const [date, setDate] = useState("");
  const [requestNumber, setRequestNumber] = useState("");
  const [requestDetails, setRequestDetails] = useState(null);
  const [issuingQuantities, setIssuingQuantities] = useState({});
  const [selectedLocations, setselectedLocations] = useState({});
  const [issuingRemarks, setIssuingRemarks] = useState({});
  const [locations, setLocations] = useState([]);
  const [profileDetails, setProfileDetails] = useState(null); // State for profile details
  const code = localStorage.getItem('code');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');
  const name = localStorage.getItem('name');
  const token = localStorage.getItem('token');
  const badgeId = localStorage.getItem('badgeId');
  const navigate = useNavigate();
  const location = useLocation();
  const requestNumberState = location?.state?.requestNumber;
  const projectRequirementId = location?.state?.projectRequirementId;
  const type = location?.state?.type;
  const [itemLocations, setItemLocations] = useState({});

  useEffect(() => {
    if (type === "IR" && requestNumberState) {
      setRequestNumber(requestNumberState);
      fetchRequestDetails(requestNumberState);          // existing
    }
  
    if (type === "PR" && projectRequirementId) {
      fetchPendingReqDetails(projectRequirementId);    // 🔹 new flow
    }
  }, [type, requestNumberState, projectRequirementId]);
  
  useEffect(() => {
    fetchProfileDetails();
    fetchLocations();
  }, []);
  const fetchProfileDetails = async () => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getProfile`, { params: { email, role } });
      setProfileDetails(response.data?.[0] || null); // Assume the first row contains the profile data
    } catch (err) {
      console.error('Error fetching profile details:', err);
      setProfileDetails(null);
    }
  };
  const fetchLocations = () => {
    apiClient
      .get(SERVER_BASE + "/getLocations", {
        params: { code }, headers: {
          Authorization: `Bearer ${token}`, // Add the JWT token in the header
        }
      })
      .then((response) =>
        setLocations(
          response.data.map((location) => ({
            value: location.location,
            label: location.location,
          }))
        )
      )
      .catch((error) => console.error(error));
  };
  const fetchLocationsForItem = async (itemId, deptOverride) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getLocationsByItem`, {
        params: {
          itemId,
          department: deptOverride,
          code
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.map((loc) => ({
        value: loc.location,
        label: loc.location
      }));
    } catch (error) {
      console.error("Error fetching item-specific locations:", error);
      return [];
    }
  };

  // Fetch request details
  const fetchRequestDetails = async (requestNumberState) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getRequestDetails`, {
        params: { requestNumber: requestNumberState },
      });

      setRequestDetails(response.data);

      // Init quantities
      const initialQuantities = {};
      const locationsMap = {};

      for (const item of response.data.inventory) {
        initialQuantities[item.itemName] = "";

        // 🔥 Fetch item-specific locations
        const locs = await fetchLocationsForItem(
          item.itemId,
          response.data.issuerDepartment
        );

        locationsMap[item.itemName] = locs;
      }

      setIssuingQuantities(initialQuantities);
      setItemLocations(locationsMap);

    } catch (error) {
      console.error("Error fetching request details:", error);
      alert(error.response?.data?.message || "Failed to fetch request details.");
    }
  };
  const fetchPendingReqDetails = async (projectRequirementId) => {
    try {
      const response = await apiClient.get(
        `${SERVER_BASE}/getPendingReqDetails`,
        { params: { projectRequirementId } }
      );
  
      // response should already be normalized (we'll shape backend like IR)
      setRequestDetails(response.data);
      setRequestNumber(response.data?.requestNumber)
      // Init quantities & locations exactly same way
      const initialQuantities = {};
      const locationsMap = {};
  
      for (const item of response.data.inventory) {
        initialQuantities[item.itemName] = "";
  
        const locs = await fetchLocationsForItem(
          item.itemId,
          response.data.issuerDepartment
        );
  
        locationsMap[item.itemName] = locs;
      }
  
      setIssuingQuantities(initialQuantities);
      setItemLocations(locationsMap);
  
    } catch (error) {
      console.error("Error fetching pending requirement details:", error);
      alert(error.response?.data?.message || "Failed to fetch pending requirement details.");
    }
  };
  

  // Handle issuing quantity changes
  const handleQuantityChange = (itemName, value) => {
    setIssuingQuantities((prevQuantities) => ({
      ...prevQuantities,
      [itemName]: value, // Update issuing quantity only for this specific itemName
    }));
  };
  // Handle issuing quantity changes
  const handleRemarksChange = (itemName, value) => {
    setIssuingRemarks((prevRemarks) => ({
      ...prevRemarks,
      [itemName]: value, // Update issuing quantity only for this specific itemName
    }));
  };
  const handleLocationChange = async (itemName, selected) => {
    if (!selected) {
      setselectedLocations(prev => {
        const copy = { ...prev };
        delete copy[itemName];
        return copy;
      });
    }

    setselectedLocations((prev) => ({
      ...prev,
      [itemName]: selected,
    }));

    // find the item in requestDetails
    const item = requestDetails.inventory.find((inv) => inv.itemName === itemName);
    if (!item) return;

    // fetch new available quantity
    const qty = await fetchAvailableQuantity(item.itemId, selected.value, "As Stock");

    // update requestDetails with new availableQuantity
    setRequestDetails((prev) => ({
      ...prev,
      inventory: prev.inventory.map((inv) =>
        inv.itemName === itemName ? { ...inv, availableQuantity: qty } : inv
      ),
    }));
  };

  const fetchAvailableQuantity = async (itemId, location, status) => {
    try {
      const response = await apiClient.get(`${SERVER_BASE}/getAvailableQuantity`, {
        params: { item: itemId, location, status, code, department: requestDetails?.issuerDepartment },
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data.availableQuantity || 0;
    } catch (error) {
      console.error("Error fetching available quantity:", error);
      return 0;
    }
  };

  // Generate issue slip
  const generateIssueSlip = async () => {
    if (!date)
      return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please select Date", showConfirmButton: false, timer: 2000 });
  
    const payload = {
      type,                                           // 🔹 IR or PR
      requestNumber: requestNumber,
      projectRequirementId: type === "PR" ? projectRequirementId : null,
  
      issueDate: date,
      requestingDepartment: requestDetails.receivingDepartment,
      issuerDepartment: requestDetails.issuerDepartment,
      issuedBy: name,
      issuedByBadge: badgeId,
      centerCode: code,
  
      issuedItems: Object.entries(issuingQuantities).map(([itemName, issuedQuantity]) => {
        const itemDetails = requestDetails.inventory.find((item) => item.itemName === itemName);
        return {
          requestItemId: itemDetails.requestItemId,
          itemName,
          issuedQuantity: (issuedQuantity) || 0,
          issuingRemark: issuingRemarks[itemName] || "",
          issuingLocation: selectedLocations[itemName] || "",
          uom: itemDetails?.uom || "",
          unit: itemDetails?.unit || "",
          itemCode: itemDetails?.itemCode || "",
          itemId: itemDetails?.itemId,
          requestedQuantity: itemDetails?.quantity,
          returnable: itemDetails?.returnable,
          availableQuantity: itemDetails?.availableQuantity
        };
      }),
    };
  
    // ----------------- 🔍 Validations -----------------
    const hasIssued = payload.issuedItems.some(item => item.issuedQuantity > 0);
    if (!hasIssued) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        position: 'top-end',
        toast: true,
        timer: 2000,
        showConfirmButton: false,
        text: 'At least one item must have an issued quantity.',
      });
      return;
    }
  
    for (const item of payload.issuedItems) {
  
      // if (item.issuedQuantity > item.requestedQuantity) {
      //   Swal.fire({
      //     icon: 'warning',
      //     title: 'Validation Error',
      //     position: 'top-end',
      //     toast: true,
      //     timer: 2000,
      //     showConfirmButton: false,
      //     text: `Issued quantity for "${item.itemName}" cannot exceed requested quantity (${item.requestedQuantity}).`,
      //   });
      //   return;
      // }
  
      const available = item.availableQuantity ?? 0;
      if (item.issuedQuantity > available) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          position: 'top-end',
          toast: true,
          timer: 2000,
          showConfirmButton: false,
          text: `Issued quantity for "${item.itemName}" cannot exceed available quantity (${item.availableQuantity}).`,
        });
        return;
      }
    }

    // ----------------- 🚀 API Call -----------------
    try {
      if (type === "IR") {
        // Normal Issue Request flow
        await apiClient.post(`${SERVER_BASE}/generateIssueSlip`, payload);
      }
  
      if (type === "PR") {
        // Project Requirement flow
        await apiClient.post(`${SERVER_BASE}/generateIssueSlipForRequirement`, payload);
      }
  
      Swal.fire({
        icon: 'success',
        title: 'Created!',
        text: 'Issue Slip Generated Successfully',
        position: 'top-end',
        toast: true,
        timer: 2000,
        showConfirmButton: false,
      });
  
      setRequestNumber("");
      setRequestDetails(null);
      setIssuingQuantities({});
      setIssuingRemarks({});
      navigate(-1);
  
    } catch (error) {
      console.error("Error generating issue slip:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || "Failed to generate issue slip",
        position: 'top-end',
        toast: true,
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };
  

  const smallSelectStyles = {
    container: (base) => ({
      ...base,
      flex: 1,
    }),
    control: (base) => ({
      ...base,
      minHeight: "34px",
      height: "34px",
      fontSize: "12px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 6px",
    }),
    input: (base) => ({
      ...base,
      fontSize: "12px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    option: (base) => ({
      ...base,
      fontSize: "12px",
      padding: "4px 8px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "34px",
    }),
  };
  return (
    <>
      <div className="formcontainer mt-5">
        <div className="d-flex align-items-end mb-3">
          <button
            type="button"
            className="btn btn-secondary btn-sm me-3"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
          </button>
          <h4 className="mb-0">Generate Issue Slip</h4>
        </div>

        {requestDetails && (
          <div>
            <p>
              <strong>Remarks:</strong> {requestDetails.remarks}
            </p>
            <div className="col-md-1 mb-2">
              <label className="form-label">Issue Date</label>
              <input type="date" className="form-control" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="row" style={{ fontSize: '13px' }}>
              {/* Requesting Department (Right) */}
              <div className="col-4 mb-4" style={{ overflowX: 'auto' }}>
                <p><strong>Requesting Department:</strong> {requestDetails.receivingDepartment}</p>
                <div className="card">
                  <div className="card-body">
                    <div className="row fw-bold mb-2">
                      <div className="col-md-6">Item</div>
                      <div className="col-md-2">Req</div>
                      <div className="col-md-4">Remarks</div>
                    </div>

                    {requestDetails.inventory.map((item) => (
                      <div className="mb-3" key={item.itemName}>
                        <div className="row mb-2 align-items-center">
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              value={item.itemName}
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                          <div className="col-md-2">
                            <input
                              type="text"
                              className="form-control"
                              value={item.quantity}
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                          <div className="col-md-4">
                            {/* <input
                              type="text"
                              className="form-control"
                              value={item.uom}
                              readOnly
                              style={{fontSize:'13px'}}
                            /> */}
                            <input
                              type="text"
                              className="form-control"
                              value={item.remarks}
                              placeholder="Requested Remarks"
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                        </div>

                        {/* Remarks below without label */}
                        {/* <div className="row">
                          <div className="col-12">
                            <input
                              type="text"
                              className="form-control"
                              value={item.remarks}
                              placeholder="Requested Remarks"
                              readOnly
                              style={{fontSize:'13px'}}
                            />
                          </div>
                        </div> */}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Issuing Department (Left) */}
              <div className="col-8 mb-4" style={{ overflowX: 'auto' }}>
                <p><strong>Issuing Department:</strong> {requestDetails.issuerDepartment}</p>
                <div className="card">
                  <div className="card-body">
                    <div className="row fw-bold mb-2">

                      <div className="col-md-3">Location</div>
                      <div className="col-md-2">Avbl</div>
                      <div className="col-md-2">Issue</div>
                      <div className="col-md-1">UOM</div>
                      <div className="col-md-1">Return</div>
                      <div className="col-md-3">Remakrs</div>
                    </div>

                    {requestDetails.inventory.map((item) => (
                      <div className="mb-3" key={item.itemName}>
                        <div className="row mb-2 align-items-center">
                          {/* Item */}


                          {/* Location (react-select) */}
                          <div className="col-md-3">
                            <Select
                              options={itemLocations[item.itemName] || []}
                              value={selectedLocations[item.itemName] || null}
                              onChange={(selected) =>
                                handleLocationChange(item.itemName, selected)
                              }
                              styles={smallSelectStyles}
                            // isDisabled={!itemLocations[item.itemName]?.length}
                            />

                          </div>

                          {/* Available */}
                          <div className="col-md-2">
                            <input
                              type="text"
                              className="form-control"
                              value={item.availableQuantity ?? 0}
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>


                          {/* Issue Qty */}
                          <div className="col-md-2">
                            <input
                              type="text"
                              className="form-control"
                              value={issuingQuantities[item.itemName] || ""}
                              onChange={(e) =>
                                handleQuantityChange(item.itemName, e.target.value)
                              }
                              min="0"
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                          <div className="col-md-1">
                            <input
                              type="text"
                              className="form-control"
                              value={item.uom}
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                          {/* Return */}
                          <div className="col-md-1">
                            <input
                              type="text"
                              className="form-control"
                              value={item.returnable === "Yes" ? "Yes" : "No"}
                              readOnly
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                          <div className="col-md-3">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Remarks"
                              value={issuingRemarks[item.itemName] || ""}
                              onChange={(e) =>
                                handleRemarksChange(item.itemName, e.target.value)
                              }
                              style={{ fontSize: '13px' }}
                            />
                          </div>
                        </div>

                        {/* Remarks below without label */}
                        {/* <div className="row">
                          <div className="col-12">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Remarks"
                              value={issuingRemarks[item.itemName] || ""}
                              onChange={(e) =>
                                handleRemarksChange(item.itemName, e.target.value)
                              }
                              style={{fontSize:'13px'}}
                            />
                          </div>
                        </div> */}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>

            <button className="btn btn-success" onClick={generateIssueSlip}>
              Generate Issue Slip
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default IssueSlip;
